import { useEffect, useMemo } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { useClientUser } from '@/hooks';
import { LIST_TARIFFS_BY_CLIENT_ID, UPSERT_DRIVER_TARIFFS, UPSERT_PRICING_OVERRIDES } from '../Tariffs/graphql';

// The tariffs/overrides/driver-mapping bundle that TariffTable and Teammates both require, plus
// the cache-updating mutations they call back into. Resolves the client from UserContext.
const useTariffBundle = () => {
    const { client_id, writableTariffClientIds } = useClientUser();
    const variables = { client_ids: writableTariffClientIds };

    const [fetchTariffs, { data }] = useLazyQuery(LIST_TARIFFS_BY_CLIENT_ID, { variables });

    const [upsertDriverTariff] = useMutation(UPSERT_DRIVER_TARIFFS, {
        update: (cache, { data: { updated } }) => {
            cache.updateQuery({ query: LIST_TARIFFS_BY_CLIENT_ID, variables }, (prev) => {
                if (!prev) return prev;
                const { driver_tariffs, ...rest } = prev;
                let clone = [...driver_tariffs];
                const update = updated.returning[0];

                const idx = clone.findIndex((m) => m.mapping_id === update.mapping_id);
                if (idx >= 0) {
                    clone[idx] = update;
                } else {
                    clone = [...clone, update];
                }

                return { ...rest, driver_tariffs: clone };
            });
        },
    });

    const [upsertPo] = useMutation(UPSERT_PRICING_OVERRIDES, {
        update: (cache, { data: { updated } }) => {
            cache.updateQuery({ query: LIST_TARIFFS_BY_CLIENT_ID, variables }, (prev) => {
                if (!prev) return prev;
                const { overrides, ...rest } = prev;
                let clone = [...overrides];
                const [update] = updated;

                const idx = clone.findIndex((po) => po.pricing_override_id === update.pricing_override_id);
                if (idx >= 0) {
                    clone[idx] = update;
                } else {
                    clone = [...clone, update];
                }

                return { ...rest, overrides: clone };
            });
        },
        onError: (e) => {
            Sentry.captureException(e);
        },
    });

    useEffect(() => {
        if (client_id) fetchTariffs();
    }, [client_id, fetchTariffs]);

    const [tariffs, overrides, driverMappings] = useMemo(() => {
        if (data) {
            const { tariffs, overrides, driver_tariffs } = data;
            return [tariffs, overrides, driver_tariffs];
        }
        return [[], [], []];
    }, [data]);

    return { tariffs, overrides, driverMappings, callbacks: { upsertDriverTariff, upsertPo } };
};

export default useTariffBundle;
